import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { FiAlertCircle, FiCheckCircle, FiClock, FiRefreshCw, FiRotateCcw, FiTrash2, FiXCircle } from 'react-icons/fi';
import { Card, Typography, Button, Chip, Spinner } from "@material-tailwind/react";
import QueueStatistics from './queue/QueueStatistics';

const { rootUrl } = require('../../config/config');

const QueueManager = () => {
    const { user: authUser, token } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [queueStats, setQueueStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newJob, setNewJob] = useState({ name: '', data: '' });
    const [refreshing, setRefreshing] = useState(false);
    const [showFailedOnly, setShowFailedOnly] = useState(false);
    const [cancellingJobs, setCancellingJobs] = useState(new Set());
    const [retryingJobs, setRetryingJobs] = useState(new Set());

    const requestHeaders = useMemo(() => ({
        Authorization: `Bearer ${token}`,
        'X-User': authUser?.username || ''
    }), [token, authUser?.username]);

    const fetchJobs = useCallback(async (failedOnly = showFailedOnly) => {
        try {
            const res = await axios.get(`${rootUrl}/api/queue/jobs`, {
                params: failedOnly ? { status: 'failed' } : undefined,
                headers: requestHeaders
            });
            setJobs(Array.isArray(res.data) ? res.data : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching jobs:', err);
            setError('Failed to fetch jobs');
        }
    }, [requestHeaders, showFailedOnly]);

    const fetchQueueStats = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/queue/status`, {
                headers: requestHeaders
            });
            setQueueStats(res.data);
        } catch (err) {
            console.error('Error fetching queue stats:', err);
        }
    }, [requestHeaders]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchJobs(showFailedOnly), fetchQueueStats()]);
            setLoading(false);
        };

        loadData();
    }, [fetchJobs, fetchQueueStats, showFailedOnly]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchJobs(showFailedOnly);
            fetchQueueStats();
        }, 10000);

        return () => clearInterval(interval);
    }, [fetchJobs, fetchQueueStats, showFailedOnly]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchJobs(showFailedOnly), fetchQueueStats()]);
        setRefreshing(false);
    };

    const handleAddJob = async (e) => {
        e.preventDefault();

        try {
            await axios.post(`${rootUrl}/api/queue/jobs`, {
                name: newJob.name,
                data: newJob.data ? JSON.parse(newJob.data) : {},
                user: authUser?.username || ''
            }, {
                headers: requestHeaders
            });

            setNewJob({ name: '', data: '' });
            await fetchJobs(showFailedOnly);
            await fetchQueueStats();
        } catch (err) {
            setError(`Failed to add job: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleCancelJob = async (id) => {
        if (!window.confirm('Are you sure you want to cancel this job?')) return;

        setCancellingJobs(prev => new Set([...prev, id]));
        try {
            await axios.delete(`${rootUrl}/api/queue/jobs/${id}`, {
                headers: requestHeaders
            });
            await Promise.all([fetchJobs(showFailedOnly), fetchQueueStats()]);
        } catch (err) {
            setError(`Failed to cancel job: ${err.response?.data?.error || err.message}`);
        } finally {
            setCancellingJobs(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleRetryJob = async (id) => {
        setRetryingJobs(prev => new Set([...prev, id]));
        try {
            await axios.post(`${rootUrl}/api/queue/jobs/${id}/retry`, {}, {
                headers: requestHeaders
            });
            await Promise.all([fetchJobs(showFailedOnly), fetchQueueStats()]);
        } catch (err) {
            setError(`Failed to retry job: ${err.response?.data?.error || err.message}`);
        } finally {
            setRetryingJobs(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    const handleTestDiscordJob = async () => {
        try {
            await axios.post(`${rootUrl}/api/queue/test-discord`, {}, {
                headers: requestHeaders
            });
            await Promise.all([fetchJobs(showFailedOnly), fetchQueueStats()]);
        } catch (err) {
            setError(`Failed to create test job: ${err.response?.data?.error || err.message}`);
        }
    };

    const getJobStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'blue';
            case 'processing':
                return 'orange';
            case 'completed':
                return 'green';
            case 'failed':
                return 'red';
            case 'cancelled':
                return 'gray';
            case 'resolved':
                return 'blue-gray';
            default:
                return 'blue-gray';
        }
    };

    const getJobStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <FiClock className="w-4 h-4" />;
            case 'processing':
                return <FiRefreshCw className="w-4 h-4 animate-spin" />;
            case 'completed':
                return <FiCheckCircle className="w-4 h-4" />;
            case 'failed':
                return <FiXCircle className="w-4 h-4" />;
            case 'cancelled':
            case 'resolved':
                return <FiAlertCircle className="w-4 h-4" />;
            default:
                return <FiClock className="w-4 h-4" />;
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-3">
                <div className="flex gap-2">
                    <Button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        size="sm"
                        variant="outlined"
                        className="flex items-center gap-2"
                    >
                        <FiRefreshCw className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </Button>
                    <Button
                        onClick={() => setShowFailedOnly(prev => !prev)}
                        size="sm"
                        variant={showFailedOnly ? 'filled' : 'outlined'}
                        color={showFailedOnly ? 'red' : 'blue-gray'}
                        className="flex items-center gap-2"
                    >
                        <FiXCircle className="w-4 h-4" />
                        {showFailedOnly ? 'Showing Failed' : 'Show Failed Tasks'}
                    </Button>
                    <Button
                        onClick={handleTestDiscordJob}
                        size="sm"
                        color="blue"
                        className="flex items-center gap-2"
                    >
                        Test Discord Job
                    </Button>
                </div>
                <Typography variant="small" color="gray">
                    {showFailedOnly ? `Failed tasks: ${jobs.length}` : `Visible jobs: ${jobs.length}`}
                </Typography>
            </div>

            {error && (
                <Card className="bg-red-50 border border-red-200 p-4">
                    <Typography color="red" className="text-sm">
                        {error}
                    </Typography>
                </Card>
            )}

            <QueueStatistics queueStats={queueStats} formatTimestamp={formatTimestamp} />

            <Card className="p-6">
                <Typography variant="h6" color="blue-gray" className="mb-4">
                    Add New Job
                </Typography>
                <form onSubmit={handleAddJob} className="flex gap-4 flex-wrap">
                    <div className="flex-1 min-w-48">
                        <input
                            type="text"
                            placeholder="Job Name (e.g., discord-trans-message)"
                            value={newJob.name}
                            onChange={e => setNewJob({ ...newJob, name: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <div className="flex-2 min-w-64">
                        <input
                            type="text"
                            placeholder="Job Data (JSON format)"
                            value={newJob.data}
                            onChange={e => setNewJob({ ...newJob, data: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <Button type="submit" color="blue" className="flex items-center gap-2">
                        Add Job
                    </Button>
                </form>
            </Card>

            <Card className="p-6">
                <Typography variant="h6" color="blue-gray" className="mb-4">
                    {showFailedOnly ? `Failed Queue Tasks (${jobs.length})` : `Queue Jobs (${jobs.length})`}
                </Typography>
                <div className="overflow-x-auto">
                    <table className="w-full table-fixed text-left">
                        <colgroup>
                            <col className="w-20" />
                            <col className="w-52" />
                            <col className="w-32" />
                            <col className="w-32" />
                            <col className="w-56" />
                            <col />
                            <col className="w-32" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        ID
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Name
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Status
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Retry Count
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Created
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Error
                                    </Typography>
                                </th>
                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-3">
                                    <Typography variant="small" color="blue-gray" className="font-bold opacity-70">
                                        Actions
                                    </Typography>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-6 text-center text-gray-500">
                                        {showFailedOnly ? 'No failed tasks found' : 'No jobs in queue'}
                                    </td>
                                </tr>
                            ) : (
                                jobs.map(job => (
                                    <tr key={job.id} className="even:bg-blue-gray-50/50 align-top">
                                        <td className="p-3">
                                            <Typography variant="small" color="blue-gray" className="font-mono">
                                                {job.id}
                                            </Typography>
                                        </td>
                                        <td className="p-3">
                                            <Typography variant="small" color="blue-gray" className="font-medium">
                                                {job.name}
                                            </Typography>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                {getJobStatusIcon(job.status)}
                                                <Chip
                                                    size="sm"
                                                    variant="filled"
                                                    color={getJobStatusColor(job.status)}
                                                    value={job.status}
                                                />
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <Typography variant="small" color="blue-gray">
                                                {job.retry_count ?? 0}
                                            </Typography>
                                        </td>
                                        <td className="p-3">
                                            <Typography variant="small" color="blue-gray">
                                                {formatTimestamp(job.created_at || job.createdon)}
                                            </Typography>
                                        </td>
                                        <td className="p-3">
                                            <Typography
                                                variant="small"
                                                color={job.status === 'failed' ? 'red' : 'gray'}
                                                className="break-words whitespace-normal"
                                                title={job.error_message || 'None'}
                                            >
                                                {job.error_message || 'None'}
                                            </Typography>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex flex-nowrap items-start gap-2">
                                                {job.status === 'failed' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        color="blue"
                                                        onClick={() => handleRetryJob(job.id)}
                                                        disabled={retryingJobs.has(job.id)}
                                                        className="flex items-center gap-1 whitespace-nowrap px-3"
                                                    >
                                                        {retryingJobs.has(job.id) ? (
                                                            <Spinner className="h-3 w-3" />
                                                        ) : (
                                                            <FiRotateCcw className="h-3 w-3" />
                                                        )}
                                                        Retry
                                                    </Button>
                                                )}
                                                {job.status === 'pending' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outlined"
                                                        color="red"
                                                        onClick={() => handleCancelJob(job.id)}
                                                        disabled={cancellingJobs.has(job.id)}
                                                        className="flex items-center gap-1 whitespace-nowrap px-3"
                                                    >
                                                        {cancellingJobs.has(job.id) ? (
                                                            <Spinner className="h-3 w-3" />
                                                        ) : (
                                                            <FiTrash2 className="h-3 w-3" />
                                                        )}
                                                        Cancel
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default QueueManager;
